import { Drawer } from "@mui/material";

import { Box, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { styled } from "@mui/system";
import { CloudLabel } from "components/atoms/CloudLabel";
import { DrawerContent } from "components/atoms/drawer/DrawerContent";
import { DrawerFooter } from "components/atoms/drawer/DrawerFooter";
import { DrawerHeader } from "components/atoms/drawer/DrawerHeader";
import { DrawerWrapper } from "components/atoms/drawer/DrawerWrapper";
import { Calendar } from "react-date-range";

interface Props {
  open: boolean;
  onClose: () => void;
  startDate: Date;
  endDate: Date;
  setBothDates: (start: Date, end: Date) => void;
}

export const DisinfectionTimeRangeFilterPanel = ({
  open,
  onClose,
  startDate,
  endDate,
  setBothDates,
}: Props) => {
  const StyledCalendarWrapper = styled(Box)(({ theme }) => ({
    "& .rdrCalendarWrapper": {
      fontFamily: "Industry-Bold",
    },
    "& .rdrMonthAndYearPickers select": {
      fontFamily: "Industry-Bold",
      fontSize: "16px",
    },
    "& .rdrSelected, & .rdrDayStartEdge, & .rdrDayEndEdge, & .rdrDayToday .rdrDayNumber span:after":
      {
        background: theme.palette.primary.main,
      },
    "& .rdrDayStartPreview, & .rdrDayInPreview, & .rdrDayEndPreview": {
      background: "transparent",
      border: "none",
    },
  }));

  const getDiffInDays = (start: Date, end: Date) => {
    return Math.floor(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
  };

  const determineSelectedRange = () => {
    const startCopy = new Date(startDate);
    startCopy.setHours(0, 0, 0, 0);
    const endCopy = new Date(endDate);
    endCopy.setHours(23, 59, 59, 999);

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (
      getDiffInDays(startCopy, endCopy) === 0 &&
      endCopy.getTime() === today.getTime()
    )
      return "day";

    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(today.getDate() - 6);
    lastWeekStart.setHours(0, 0, 0, 0);
    if (
      startCopy.getTime() === lastWeekStart.getTime() &&
      endCopy.getTime() === today.getTime()
    )
      return "week";

    const lastMonthStart = new Date(today);
    lastMonthStart.setMonth(today.getMonth() - 1);
    lastMonthStart.setHours(0, 0, 0, 0);
    if (
      startCopy.getTime() === lastMonthStart.getTime() &&
      endCopy.getTime() === today.getTime()
    )
      return "month";

    const lastYearStart = new Date(today);
    lastYearStart.setFullYear(today.getFullYear() - 1);
    lastYearStart.setHours(0, 0, 0, 0);
    if (
      startCopy.getTime() === lastYearStart.getTime() &&
      endCopy.getTime() === today.getTime()
    )
      return "year";

    return "";
  };

  return (
    <Drawer
      sx={{
        minWidth: 500,
      }}
      anchor={"right"}
      open={open}
      onClose={onClose}
    >
      <DrawerWrapper>
        <DrawerHeader header="Timeframe" onClose={onClose} />
        <DrawerContent>
          <CloudLabel sx={{ paddingTop: "0" }}>
            {"Start date (From start of day)"}
          </CloudLabel>
          <StyledCalendarWrapper
            display="flex"
            justifyContent="center"
            flexDirection="row"
            alignItems="center"
          >
            <Calendar
              date={startDate}
              onChange={(newDate) => setBothDates(newDate, endDate)}
              maxDate={endDate}
            />
          </StyledCalendarWrapper>
          <CloudLabel>{"End date (To end of day)"}</CloudLabel>
          <StyledCalendarWrapper
            display="flex"
            justifyContent="center"
            flexDirection="row"
            alignItems="center"
          >
            <Calendar
              date={endDate}
              onChange={(newDate) => setBothDates(startDate, newDate)}
              minDate={startDate}
              maxDate={new Date()}
            />
          </StyledCalendarWrapper>
          <DrawerFooter
            overrideWarning
            error={false ? "Filter query failed - try again." : undefined}
            action={{
              label: `Show disinfections (${Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1} days)`,
              onClick: onClose,
              loading: false,
            }}
            secondaryAction={{
              label: "Reset to default",
              onClick: () => {
                const today = new Date();
                today.setHours(23, 59, 59, 999);
                const monthAgo = new Date(today);
                monthAgo.setMonth(monthAgo.getMonth() - 1);
                monthAgo.setHours(0, 0, 0, 0);
                setBothDates(monthAgo, today);
              },
              color: "primary",
            }}
          />
          <CloudLabel>Quick filters</CloudLabel>
          <RadioGroup
            key={`${startDate.toISOString()}-${endDate.toISOString()}`}
            value={determineSelectedRange()}
            onChange={(e, value) => {
              const end = new Date();
              end.setHours(23, 59, 59, 999);
              const start = new Date();
              start.setHours(0, 0, 0, 0);

              switch (value) {
                case "day":
                  break;
                case "week":
                  start.setDate(end.getDate() - 6);
                  break;
                case "month":
                  start.setMonth(end.getMonth() - 1);
                  break;
                case "year":
                  start.setFullYear(end.getFullYear() - 1);
                  break;
              }
              setBothDates(start, end);
            }}
            name="time-range"
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <FormControlLabel
              value={"day"}
              control={<Radio />}
              label={"Today"}
            />
            <FormControlLabel
              value={"week"}
              control={<Radio />}
              label={"Last week"}
            />
            <FormControlLabel
              value={"month"}
              control={<Radio />}
              label={"Last month"}
            />
            <FormControlLabel
              value={"year"}
              control={<Radio />}
              label={"Last year"}
            />
          </RadioGroup>
        </DrawerContent>
      </DrawerWrapper>
    </Drawer>
  );
};
